import React, { Component } from 'react';
import { Flex, Heading, Text, Button, Card, Lookup, Box } from 'core/components';
import AdminTable from 'app/components/admin/AdminTable';
import SiteLink from 'app/components/links/SiteLink';
import InterfaceFlowStatus from 'app/views/settings/interfaces/InterfaceFlowStatus';
import InterfaceCollection from 'app/stores/interface/InterfaceCollection';
import { InterfaceNameSnmpIdAndDescription } from 'app/components/Interface';
import DeviceLink from 'app/components/links/DeviceLink';
import { ReactComponent as PeeringdbIcon } from 'app/assets/logos/peeringdb-icon.svg';
import InterfaceClassificationKv from 'app/components/interfaces/InterfaceClassificationKv';
import { formatBytesGreek } from 'core/util/greekPrefixing';
import { inject, observer } from 'mobx-react';

const Overridden = ({ isOverridden }) => {
  if (!isOverridden) {
    return null;
  }

  return <Text muted>(Overridden)</Text>;
};

const ColumnOverrideRendererHOC =
  (field) =>
  ({ model, value }) => {
    const hasOverridden = model.overriddenFields.includes(field);

    return (
      <>
        {value}
        <Overridden isOverridden={hasOverridden} />
      </>
    );
  };

@inject('$devices')
@observer
class InterfaceInventoryToolResult extends Component {
  state = {
    collection: new InterfaceCollection()
  };

  componentDidMount() {
    const { collection } = this.state;
    const { tool } = this.props;
    const { raw_result = {} } = tool;
    collection.add(raw_result);
  }

  get columns() {
    const { $devices } = this.props;
    const columns = [
      {
        label: 'Interface',
        selectorLabel: 'Interface (combined)',
        id: 'interface',
        value: 'interface',
        sortField: 'interface_description',
        flexBasis: 200,
        minWidth: 100,
        renderer: ({ model }) => {
          const deviceSummary = $devices.activeDeviceSummariesById[model.get('device_id')];
          const hasOverridden = model.overriddenFields.some((field) =>
            ['snmp_id', 'interface_description', 'snmp_alias'].includes(field)
          );

          if (deviceSummary) {
            return (
              <Box overflow="hidden">
                <InterfaceNameSnmpIdAndDescription
                  interface_description={model.get('interface_description')}
                  snmp_alias={model.get('snmp_alias')}
                  snmp_id={model.get('snmp_id')}
                  device_name={deviceSummary.device_name}
                />
                <Overridden isOverridden={hasOverridden} />
              </Box>
            );
          }
          return `Interface (${model.get('snmp_id')}) on archived device (${model.get('device_id')})`;
        }
      },
      {
        label: 'SNMP ID',
        id: 'snmp_id',
        value: 'snmp_id',
        name: 'snmp_id',
        minWidth: 88,
        flexBasis: 50,
        renderer: ColumnOverrideRendererHOC('snmp_id'),
        ellipsis: false
      },
      {
        label: 'Name',
        selectorLabel: 'Interface Name',
        id: 'interface_description',
        value: 'interface_description',
        name: 'interface_description',
        flexBasis: 150,
        renderer: ColumnOverrideRendererHOC('interface_description')
      },
      {
        label: 'Description',
        selectorLabel: 'Interface Description',
        id: 'snmp_alias',
        value: 'snmp_alias',
        name: 'snmp_alias',
        flexBasis: 200,
        renderer: ColumnOverrideRendererHOC('snmp_alias')
      },
      {
        label: 'Device',
        name: 'deviceName',
        id: 'device',
        value: 'device',
        computed: true,
        flexBasis: 115,
        renderer: ({ value }) => (value ? <DeviceLink name={value} /> : <Text muted>None</Text>)
      },
      {
        label: 'Site',
        name: 'siteName',
        id: 'site',
        value: 'site',
        computed: true,
        renderer: ({ value, model }) =>
          value ? <SiteLink siteId={model.siteId}>{value}</SiteLink> : <Text muted>None</Text>
      },
      {
        label: 'Classification',
        name: 'connectivity_type',
        id: 'classification',
        value: 'classification',
        minWidth: 130,
        ellipsis: false,
        renderer: ({ value, model }) => {
          const provider = model.get('provider');
          const networkBoundary = model.get('network_boundary');

          return (
            <InterfaceClassificationKv
              networkBoundary={networkBoundary}
              connectivity={value}
              provider={provider}
              overriddenFields={model.overriddenFields}
              showLinks
            />
          );
        }
      },
      {
        label: 'Capacity',
        name: 'snmp_speed',
        id: 'capacity',
        value: 'capacity',
        flexBasis: 80,
        minWidth: 90,
        renderer: ({ value, model }) => {
          const hasOverridden = model.overriddenFields.includes('snmp_speed');
          const formattedValue = value !== '0' ? formatBytesGreek(value * 1000000, '', 0.9991) : '';
          return (
            <Box>
              <Text as="div">{formattedValue}</Text>
              <Overridden isOverridden={hasOverridden} />
            </Box>
          );
        }
      },
      {
        label: 'IP Address',
        name: 'interface_ip',
        id: 'ip',
        value: 'ip',
        flexBasis: 90,
        minWidth: 100,
        renderer: ColumnOverrideRendererHOC('interface_ip')
      },
      {
        label: (
          <Box>
            <Text>Traffic In</Text>
            <Text small muted as="div">
              Flow
            </Text>
            <Text small muted as="div">
              SNMP
            </Text>
          </Box>
        ),
        selectorLabel: 'Traffic In',
        name: 'flowInboundValue',
        computed: true,
        id: 'trafficIn',
        value: 'trafficIn',
        ellipsis: false,
        minWidth: 110,
        flexBasis: 80,
        renderer: ({ model }) => <InterfaceFlowStatus model={model} status={model.flowInbound} hideThresholdText />
      },
      {
        label: (
          <>
            <PeeringdbIcon style={{ width: 15, height: 15 }} />
            <Text pl={1}>PeeringDB IX</Text>
          </>
        ),
        name: 'ix_id',
        id: 'ix_id',
        value: 'ix_id',
        selectorLabel: 'PeeringDB IX',
        renderer: ({ value }) => {
          if (value) {
            return <Lookup lookup="ix_id" value={value} />;
          }
          return '';
        }
      },
      {
        label: (
          <Box>
            <Text>Traffic Out</Text>
            <Text small muted as="div">
              Flow
            </Text>
            <Text small muted as="div">
              SNMP
            </Text>
          </Box>
        ),
        selectorLabel: 'Traffic Out',
        name: 'flowOutboundValue',
        computed: true,
        id: 'trafficOut',
        value: 'trafficOut',
        ellipsis: false,
        minWidth: 110,
        flexBasis: 80,
        renderer: ({ model }) => <InterfaceFlowStatus model={model} status={model.flowOutbound} hideThresholdText />
      }
    ];

    return columns;
  }

  render() {
    const { collection } = this.state;
    const { tool, onCloseToolResult } = this.props;

    return (
      <Card
        p={0}
        className="tool-result-interface-inventory"
        display="flex"
        flexDirection="column"
        gap={2}
        minHeight={600}
        height="100%"
        overflow="hidden"
      >
        <Flex alignItems="center" justifyContent="space-between" p={2} pb={0}>
          <Heading level={5} m={0} flex={1}>
            {tool.title || 'Interface Inventory Result'}
          </Heading>
          <Flex alignItems="center" gap={1}>
            <Button text="Close" small icon="cross" onClick={() => onCloseToolResult(tool)} color="muted" minimal />
          </Flex>
        </Flex>
        <Flex flexDirection="column" px={1} pb={1} flex={1}>
          <AdminTable
            collection={collection}
            useDiscreteFilters={false}
            rowHeight={55}
            columns={this.columns}
            tabPrefId="interfaces"
          />
        </Flex>
      </Card>
    );
  }
}

export default InterfaceInventoryToolResult;
